import axios from 'axios';
import { api } from './client';

interface ProviderResponse {
  provider: string | null;
}

/**
 * Public tenant-discovery endpoint — takes a work email and returns the exact
 * Cognito provider name to federate with, or null for every unavailable case
 * (invalid email, not invited, connection disabled, etc.).
 *
 * Uses a bare axios call: the user isn't signed in yet, so we must skip the
 * auth interceptor in client.ts (which reads the Cognito session). We still
 * reuse the app's configured API base URL.
 */
export const getMicrosoftEntraProvider = async (
  email: string
): Promise<string | null> => {
  const { data } = await axios.get<ProviderResponse>(
    '/auth/microsoft-entra/provider',
    { baseURL: api.defaults.baseURL, params: { email } }
  );
  return data.provider;
};

export interface MicrosoftEntraConnectionStatus {
  companyId: number;
  enabled: boolean;
  tenantId: string | null;
  providerName: string | null;
}

/**
 * Whether a company signs in through Entra. `enabled` is the yes/no; a company
 * whose connection is switched off reads the same as one that never had it.
 * Authenticated call — an admin may only read their own company (superusers any).
 */
export const getMicrosoftEntraConnectionStatus = async (
  companyId: number
): Promise<MicrosoftEntraConnectionStatus> => {
  const { data } = await api.get<MicrosoftEntraConnectionStatus>(
    `/auth/microsoft-entra/connections/${companyId}`
  );
  return data;
};
